<template>
  <div
    v-if="shouldShowErrors"
    ref="errorListRef"
    :id="componentId"
    class="st-error-list"
    role="alert"
    :aria-live="ariaLive"
    aria-atomic="true"
    :aria-label="fullErrorAnnouncement"
    tabindex="-1"
  >
    <div class="st-error-list__header">
      <StIcon
        name="warning-triangle"
        :icon-props="{ color: '#CC4039' }"
        :aria-hidden="true"
      />
      <component :is="headingTag" :id="titleId" class="st-error-list__title">
        {{ validationErrorCountText }}
      </component>
    </div>

    <ul class="st-error-list__list">
      <li
        v-for="(error, index) in sortedErrors"
        :key="error.fieldId || `validation-error-${index}`"
        class="st-error-list__item"
      >
        <button
          v-if="error.fieldId && fieldExistsInDOM(error.fieldId)"
          type="button"
          class="st-error-list__link"
          @click="focusField(error.fieldId)"
          :aria-describedby="error.fieldId"
          :aria-label="`Gå till fält: ${error.message}`"
        >
          {{ error.message }}
          <strong
            v-if="error.errorCode"
            class="st-error-list__text--highlighted"
          >
            {{ error.errorCode }}
          </strong>
        </button>
        <span v-else class="st-error-list__text">
          {{ error.message }}
          <strong
            v-if="error.errorCode"
            class="st-error-list__text--highlighted"
          >
            {{ error.errorCode }}
          </strong>
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted } from "vue";
import StIcon from "../icon/Icon.vue";

// Template ref for the error list container
const errorListRef = ref(null);

const props = defineProps({
  /**
   * Array of error objects with message and optional fieldId for linking.
   * This is the primary prop for passing errors to the component.
   * @type {Array<{message: string, fieldId?: string}>}
   */
  errors: {
    type: Array,
    default: () => [],
    validator: (errors) => {
      return errors.every(
        (error) =>
          error && typeof error.message === "string" && error.message.length > 0
      );
    },
  },

  /**
   * Whether to automatically focus the error list when errors appear
   */
  autoFocusErrorList: {
    type: Boolean,
    default: true,
  },

  /**
   * Whether the user has attempted to submit the form at least once.
   * Controls smart validation behavior - blur errors only show after first submit attempt.
   */
  hasAttemptedSubmit: {
    type: Boolean,
    default: false,
  },

  /**
   * Custom title text. If not provided, will generate based on error count
   */
  title: {
    type: String,
    default: "",
  },

  /**
   * Aria-live setting for the error list.
   * Use 'assertive' for critical errors, 'polite' for form validation
   */
  ariaLive: {
    type: String,
    default: "polite",
    validator: (value) => ["polite", "assertive", "off"].includes(value),
  },

  /**
   * Custom ID for the error list container
   */
  id: {
    type: String,
    default: null,
  },

  /**
   * Automatically detect and sort errors by field order in the DOM.
   */
  autoSortOrder: {
    type: Boolean,
    default: false,
  },

  /**
   * HTML tag to use for the error count heading. Use 'div' to remove semantic heading.
   * Choose the appropriate level based on your page's heading hierarchy.
   */
  headingTag: {
    type: String,
    default: "h2",
    validator: (value) =>
      ["h1", "h2", "h3", "h4", "h5", "h6", "div"].includes(value),
  },
});

// Generate unique ID for this instance
const componentId = ref(
  props.id || `st-error-list-${Math.random().toString(36).substring(2, 11)}`
);
const titleId = computed(() => `${componentId.value}-title`);

// Auto-detected field order based on DOM structure
const autoDetectedFieldOrder = ref([]);

// Track previous error count to determine if this is initial errors vs revalidation
const previousErrorCount = ref(0);

/**
 * Automatically detect form fields in the DOM and create a field order
 * based on their position in the document
 */
const detectFormFieldOrder = () => {
  try {
    const errorFieldIds = props.errors
      .map((error) => error.fieldId)
      .filter(Boolean);

    if (errorFieldIds.length === 0) {
      autoDetectedFieldOrder.value = [];
      return;
    }

    // Find all form elements in the document
    const formElements = document.querySelectorAll(
      [
        "input[id]",
        "textarea[id]",
        "select[id]",
        "button[id]",
        "div[id]",
        '[role="textbox"][id]',
        '[role="combobox"][id]',
        '[role="listbox"][id]',
      ].join(", ")
    );

    // Create a map of element IDs to their DOM position
    const elementPositions = new Map();
    formElements.forEach((element, index) => {
      if (element.id && errorFieldIds.includes(element.id)) {
        elementPositions.set(element.id, index);
      }
    });

    // Sort field IDs by their DOM position
    const sortedFieldIds = errorFieldIds
      .filter((fieldId) => elementPositions.has(fieldId))
      .sort((a, b) => elementPositions.get(a) - elementPositions.get(b));

    // Add any field IDs that weren't found in DOM to the end
    const missingFieldIds = errorFieldIds.filter(
      (fieldId) => !elementPositions.has(fieldId)
    );

    autoDetectedFieldOrder.value = [...sortedFieldIds, ...missingFieldIds];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("ErrorList: Failed to auto-detect field order:", error);
    autoDetectedFieldOrder.value = [];
  }
};

const shouldShowErrors = computed(() => {
  // If no errors, don't show
  if (props.errors.length === 0) return false;

  // Always show errors if user has attempted to submit
  // This enables both initial form submission errors and subsequent blur validation errors
  return props.hasAttemptedSubmit;
});

const validationErrorCountText = computed(() => {
  if (props.title) {
    return props.title;
  }

  const count = props.errors.length;
  return `${count} fel hittades`;
});

const sortedErrors = computed(() => {
  if (!props.autoSortOrder || !autoDetectedFieldOrder.value?.length) {
    return props.errors;
  }

  // Sort errors according to the auto-detected field order
  return [...props.errors].sort((a, b) => {
    // Errors without fieldId (general errors) go to the end
    if (!a.fieldId && !b.fieldId) return 0;
    if (!a.fieldId) return 1;
    if (!b.fieldId) return -1;

    // Get positions in the auto-detected field order array
    const aIndex = autoDetectedFieldOrder.value.indexOf(a.fieldId);
    const bIndex = autoDetectedFieldOrder.value.indexOf(b.fieldId);

    // Fields not in the order array go to the end
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });
});

// Generate comprehensive error announcement for screen readers
const fullErrorAnnouncement = computed(() => {
  if (props.errors.length === 0) return "";

  // Start with error count
  const countText = `${props.errors.length} fel hittades`;

  // Add all error messages
  const errorMessages = props.errors.map((error) => error.message).join(", ");

  return `${countText}. ${errorMessages}`;
});

/**
 * Focus the field with the given ID and scroll it into view
 */
const focusField = (fieldId) => {
  if (!fieldId) return;

  try {
    const element = document.getElementById(fieldId);
    if (element) {
      // Scroll to element first
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Focus after a short delay to ensure scrolling completes
      element.focus();
      setTimeout(() => {
        // If element is not focusable, try to find the first focusable element within it
        if (document.activeElement !== element) {
          const focusableElement = element.querySelector(
            'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
          );
          if (focusableElement) {
            focusableElement.focus();
          }
        }
      }, 100);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("ErrorList: Could not focus field with ID:", fieldId, error);
  }
};

/**
 * Focus the error list itself for better screen reader accessibility
 */
const focusErrorList = () => {
  if (errorListRef.value) {
    try {
      errorListRef.value.focus();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("ErrorList: Could not focus error list container:", error);
    }
  }
};

/**
 * Determine if auto-focus should be applied based on current conditions
 */
const shouldAutoFocus = computed(() => {
  // If auto-focus is disabled, never focus
  if (!props.autoFocusErrorList) {
    return false;
  }

  return true;
});

// Auto-focus functionality when errors appear
onMounted(() => {
  if (props.autoSortOrder) {
    nextTick(() => {
      detectFormFieldOrder();
    });
  }

  // Initialize previous error count
  previousErrorCount.value = props.errors.length;

  // Auto-focus if component mounts with errors (initial display)
  if (
    shouldAutoFocus.value &&
    shouldShowErrors.value &&
    props.errors.length > 0
  ) {
    nextTick(() => {
      setTimeout(() => {
        focusErrorList();
      }, 150);
    });
  }
});

watch(
  () => props.errors,
  () => {
    if (props.autoSortOrder) {
      nextTick(() => {
        detectFormFieldOrder();
      });
    }

    // Only auto-focus if auto-focus is enabled
    if (!shouldAutoFocus.value) {
      return;
    }

    // Auto-focus when errors first appear (going from 0 to some errors)
    // This happens on initial form submission, not on blur revalidation
    const currentErrorCount = props.errors.length;
    const isInitialErrors =
      previousErrorCount.value === 0 && currentErrorCount > 0;

    if (isInitialErrors) {
      nextTick(() => {
        setTimeout(() => {
          focusErrorList();
        }, 150);
      });
    }

    // Update previous count for next comparison
    previousErrorCount.value = currentErrorCount;
  },
  { immediate: false, deep: true }
);

const fieldExistsInDOM = async (fieldId) => {
  await nextTick();
  if (!fieldId) return false;
  return document.getElementById(fieldId) !== null;
};

// Expose methods for parent components if needed
defineExpose({
  focusField,
  focusErrorList,
});
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-error-list {
  background-color: var(--danger-color-secondary);
  border: rem-calc(2) solid var(--danger-color-primary);
  border-radius: var(--border-radius);
  padding: var(--md);
  font-family: var(--font-primary);
  color: var(--danger-color-primary);
  margin-bottom: var(--lg);

  &:focus {
    outline: rem-calc(2) solid var(--border-color-quaternary);
    outline-offset: rem-calc(2);
  }

  &__header {
    display: flex;
    align-items: center;
    margin-bottom: var(--sm);
    gap: var(--sm);
  }

  &__title {
    font-size: rem-calc(16);
    font-weight: 600;
    margin: 0;
    color: var(--text-color-primary);
  }

  &__list {
    list-style: disc;
    margin: 0;
    padding-left: rem-calc(0);
    color: var(--text-color-primary);
    padding-left: rem-calc(16);
  }

  &__item {
    margin-bottom: rem-calc(2);

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__link {
    background: none;
    border: none;
    padding: rem-calc(2);
    font-family: inherit;
    font-size: rem-calc(14);
    color: var(--text-color-primary);
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
    line-height: 1.4;
    user-select: text;

    &:hover {
      text-decoration: underline;
    }

    &:focus {
      outline: var(--greyscale-700) solid
        calc(2 / var(--base-font-size, 16) * 1rem);
    }
  }

  &__text {
    font-size: rem-calc(14);
    line-height: 1.4;
    color: inherit;

    &--highlighted {
      font-weight: var(--font-weight-semi-bold);
    }
  }
}
</style>
